import React, { FC, useCallback, useContext, useMemo } from 'react';

import styled from 'styled-components';

// theme
import aampeTheme from '../../constants/aampeTheme';

// context
import { EditorContext } from '../../context/EditorContext';

// hooks
import useCMSPlaceholderQuery from '../../hooks/cms-placeholders/useCMSPlaceholderQuery';

// models
import { CMSFilterModel } from '../../models/cms-placeholder.model';

interface FormulaSidebarCMSFiltersProps {
    selectedComponent: Record<string, any> | null;
}

const FormulaSidebarCMSFilters: FC<
    React.PropsWithChildren<FormulaSidebarCMSFiltersProps>
> = ({ selectedComponent }) => {
    const { data } = useCMSPlaceholderQuery();

    const {
        editorDetails: { formula },
    } = useContext(EditorContext);

    const selected = selectedComponent?.cmsPlaceholderId;

    const getCMSNameById = useCallback(
        (id: string) =>
            data?.filterable?.find((item) => item.id === id)?.name ?? '',
        [data],
    );

    const selectedText = useMemo<string>(() => {
        return (
            data?.messageable?.find((item) => item.id === selected)?.name ?? ''
        );
    }, [data, selected]);

    return (
        <Container>
            <Body>
                <PlaceholderContainer>
                    <Heading>Cart reference:</Heading>
                    <span>{`{{${selectedText}}}`}</span>
                </PlaceholderContainer>
                {(formula?.cms_placeholder_filters ?? []).length > 0 && (
                    <FiltersContainer>
                        <Heading>Filters</Heading>
                        <Filters>
                            {(formula?.cms_placeholder_filters ?? []).map(
                                (item: CMSFilterModel) => (
                                    <FilterItem key={item.id}>
                                        <span>
                                            {getCMSNameById(
                                                item.cms_placeholder_id,
                                            )}
                                        </span>
                                        <span>{item.operator}</span>
                                        <span>{item.value}</span>
                                    </FilterItem>
                                ),
                            )}
                        </Filters>
                    </FiltersContainer>
                )}
            </Body>
        </Container>
    );
};

const Container = styled.div(() => ({
    width: '100%',
    overflow: 'auto',
}));

const Body = styled.div(() => ({
    padding: aampeTheme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    gap: aampeTheme.spacing(3),
    overflow: 'auto',
    height: '100%',
}));

const PlaceholderContainer = styled.div(() => ({
    display: 'flex',
    gap: aampeTheme.spacing(1),
}));

const FiltersContainer = styled.div(() => ({
    display: 'flex',
    flexDirection: 'column',
    gap: aampeTheme.spacing(3),
}));

const Filters = styled.div(() => ({
    display: 'flex',
    flexDirection: 'column',
    gap: aampeTheme.spacing(1),
}));

const Heading = styled.span(() => ({
    fontSize: 14,
    fontWeight: 'bold',
}));

const FilterItem = styled.div(() => ({
    display: 'flex',
    gap: aampeTheme.spacing(1),
    padding: aampeTheme.spacing(1),
    border: `1px solid ${aampeTheme.colors.bordersWG01}`,
    borderRadius: aampeTheme.borderRadius.default,
}));

export default FormulaSidebarCMSFilters;
